import './sidebar.css';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { List, ListItemIcon, ListItemButton, Box, ListItemText } from '@mui/material';
import { ShowChart, AccountTree, Share, WorkOutline, Dashboard, ExtensionTwoTone, ManageAccountsTwoTone, PublicTwoTone, Sensors, ShareTwoTone, Insights, BackupTable, MenuBook, Tune, Widgets } from '@mui/icons-material';
import { LoginContext } from '../../App';
import B2BUsersMenu from '../b2b-users/B2BUsersMenu';
import B2BLanguagesMenu from '../b2b-languages/B2BLanguagesMenu';
import B2BIndicatorsMenu from '../b2b-indicators/B2BIndicatorsMenu';
import B2BFrameworksMenu from '../b2b-frameworks/B2BFrameworksMenu';
import { useNavigate } from 'react-router-dom';
import { shouldRenderComponent } from '../b2b-common/helpers';
import useB2BTranslation from '../b2b-common/hooks/useB2BTranslation';

const Sidebar = () => {
    const { t } = useB2BTranslation('b2b-common');

    const {
        sidebarOpen, setSidebarOpen,
        marIaSideBarOpen, setMarIaSideBarOpen,
        selectedItem, setSelectedItem,
        loginData
    } = useContext(LoginContext);

    const previousLoginData = useRef(loginData);

    useEffect(() => {
        // set the default selected item
        if (!selectedItem || selectedItem === '') {
            handleItemClick('home');
        }
        // }
    }, [selectedItem]);

    useEffect(() => {
        if (JSON.stringify(previousLoginData.current) !== JSON.stringify(loginData)) {
            handleItemClick('home');
            previousLoginData.current = loginData;
        }
    }, [loginData]);

    const [isHovering, setIsHovering] = useState(false);

    const toggleMinimized = () => {
        setSidebarOpen(!sidebarOpen);
        if ((!sidebarOpen) && (marIaSideBarOpen)) {
            setMarIaSideBarOpen(false);
        }
    };

    const hasSubmenu = (item) => {
        let hasSubmenu = false;
        sidebarItems.forEach((sidebarItem) => {
            if (sidebarItem.key === item) {
                hasSubmenu = sidebarItem.submemu;
            }
        });
        return hasSubmenu;
    };

    const onClickIfDefined = (item) => {
        sidebarItems.forEach((sidebarItem) => {
            if (sidebarItem.key === item && sidebarItem.onClick) {
                sidebarItem.onClick();
            }
        });
    };

    const handleItemClick = (item) => {
        setSelectedItem(item);
        // need to check if the item has submenu, if not, close the sidebar
        if (hasSubmenu(item)) {
            if (((item === selectedItem)) ||
                ((item !== selectedItem) && !sidebarOpen)
            ) {
                toggleMinimized();
            }
        } else {
            setSidebarOpen(false);
            // if onClick is defined, call it
            onClickIfDefined(item);
        }
    };

    const handleMenu2ItemClick = (item) => {
        toggleMinimized();
    };

    const SidebarItem = ({ itemKey, icon, text, rolesAllowed, b2bProducts }) => {
        const shouldRender = shouldRenderComponent(loginData, rolesAllowed, b2bProducts);
        if (!shouldRender) {
            return null;
        }
        return (
            <ListItemButton onClick={() => handleItemClick(itemKey)}
                className={`b2b-sidebar-layer1-menu-button ${selectedItem === itemKey ? 'selected' : ''}`}
            >
                <ListItemIcon className={`b2b-sidebar-layer1-menu-icon ${selectedItem === itemKey ? 'selected' : ''}`} style={{ justifyContent: 'center' }}>
                    {icon}
                </ListItemIcon>
                <ListItemText primary={text} className={`b2b-sidebar-layer1-menu-text ${selectedItem === itemKey ? 'selected' : ''}`} sx={{ marginLeft: '20px' }} />
            </ListItemButton>
        );
    };

    const navigate = useNavigate();

    const sidebarItems = [
        { key: 'home', icon: <BackupTable />, text: t('Home'), rolesAllowed: [], submemu: false, onClick: () => { navigate('/reporting/a2030-briefing'); } },
        { key: 'users', icon: <ManageAccountsTwoTone />, text: t('Users'), rolesAllowed: ['ROLE_SUPER_ADMIN'], submemu: true },
        { key: 'languages', icon: <PublicTwoTone />, text: t('Localization'), rolesAllowed: ['ROLE_SUPER_ADMIN'], submemu: true },
        {
            key: 'plans',
            icon: <WorkOutline />, text: t('Plans'), rolesAllowed: [], submemu: false,
            onClick: () => { navigate('/plans/plans-explorer'); }
        },
        {
            key: 'dashboards',
            icon: <Dashboard />, text: t('Dashboards'), rolesAllowed: [], submemu: false,
            b2bProducts: ['B2B_CUSTOM_DASHBOARDS'],
            onClick: () => { navigate('/reporting/dashboards'); }
        },
        {
            key: 'widget-templates',
            icon: <Widgets />, text: t('Widget Templates'), rolesAllowed: ['ROLE_SUPER_ADMIN'], submemu: false,
            b2bProducts: [],
            onClick: () => { navigate('/reporting/widget-templates'); }
        },
        {
            key: 'reports',
            icon: <MenuBook />, text: t('Reports'), rolesAllowed: [], submemu: false,
            b2bProducts: [],
            onClick: () => { navigate('/reporting/create'); }
        },
        { key: 'custom_indicators', icon: <Insights />, text: t('Custom Indicators'), rolesAllowed: [], submemu: false, onClick: () => { navigate('/indicators/custom-explorer'); } },
        { key: 'product_indicators', icon: <AccountTree />, text: t('Product Indicators'), rolesAllowed: [], submemu: false, onClick: () => { navigate('/frameworks/fws-explorer'); } },
        { key: 'indicators', icon: <ShowChart />, text: t('Indicators'), rolesAllowed: ['ROLE_SUPER_ADMIN'], submemu: true },
        { key: 'frameworks', icon: <AccountTree />, text: t('Frameworks'), rolesAllowed: ['ROLE_SUPER_ADMIN'], submemu: true },
        {
            key: 'iot-integrations-customers',
            icon: <Sensors />, text: t('Integrations'), rolesAllowed: ['ROLE_ADMIN'], submemu: false,
            b2bProducts: ["B2B_IOT_SAMPLING", "B2B_IOT_FW"],
            onClick: () => {
                navigate('/iot-integrations/sensors-explorer');
            }
        },
        {
            key: 'iot-integrations-back2blu',
            icon: <ExtensionTwoTone />, text: t('B2B Integrations'), rolesAllowed: ['ROLE_SUPER_ADMIN'], submemu: false,
            b2bProducts: [],
            onClick: () => {
                navigate('/iot-integrations/sensors-master-configurations');
            }
        },
        {
            key: 'social', icon: <Share />, text: t('Social Sharing'), submemu: false,
            rolesAllowed: ['ROLE_ADMIN'],
            b2bProducts: ['B2B_SOCIAL'],
            onClick: () => {
                navigate('/social/social-posts-explorer');
            }
        },
        {
            key: 'social-back2blu',
            icon: <ShareTwoTone />, text: t('Social Sharing Templates'), submemu: false,
            rolesAllowed: ['ROLE_SUPER_ADMIN'],
            onClick: () => {
                navigate('/social/social-master-configurations');
            }
        },
        {
            key: 'system',
            icon: <Tune />, text: t('System'), rolesAllowed: ['ROLE_SUPER_ADMIN'], submemu: false,
            onClick: () => {
                navigate('/system/microservices-explorer');
            }
        }
    ];

    return (
        <Box className={`b2b-sidebar-container ${!sidebarOpen ? 'b2b-sidebar-closed' : 'b2b-sidebar-open'} ${(!sidebarOpen && isHovering) ? 'b2b-sidebar-hovering' : ''}`}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <Box className="b2b-sidebar-layer1-menu">
                <List>
                    {sidebarItems.map((item) => (
                        <SidebarItem key={item.key} itemKey={item.key} icon={item.icon} text={item.text} rolesAllowed={item.rolesAllowed} b2bProducts={item.b2bProducts} />
                    ))}
                </List>
                {sidebarOpen && (
                    <Box className="b2b-sidebar-layer2-menu">
                        {selectedItem === 'users' ? <B2BUsersMenu onItemSelected={handleMenu2ItemClick} /> : null}
                        {selectedItem === 'languages' ? <B2BLanguagesMenu onItemSelected={handleMenu2ItemClick} /> : null}
                        {selectedItem === 'indicators' ? <B2BIndicatorsMenu onItemSelected={handleMenu2ItemClick} /> : null}
                        {selectedItem === 'frameworks' ? <B2BFrameworksMenu onItemSelected={handleMenu2ItemClick} /> : null}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Sidebar;
